import { boolean, index, jsonb, pgTable, text, timestamp, uniqueIndex, uuid, } from "drizzle-orm/pg-core";
import { organisationIntegrations } from "./integrations";
import { conversations } from "./chat";
import { users } from "./users";
import { embeddings } from "./embeddings";
export const integrationMessages = pgTable("integrations_messages", {
    id: uuid("id").primaryKey().defaultRandom(),
    integrationType: text("integration_type").notNull().$type(),
    integrationId: uuid("integration_id")
        .notNull()
        .references(() => organisationIntegrations.id, { onDelete: "cascade" }),
    teamId: text("team_id"),
    channelId: text("channel_id"),
    threadTs: text("thread_ts"),
    messageTs: text("message_ts"),
    userId: text("user_id"),
    text: text("text_content"),
    rawPayload: jsonb("raw_payload").default({}),
    metadata: jsonb("metadata").notNull().default({}),
    expiresAt: timestamp("expires_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow()
        .$onUpdate(() => new Date()),
}, table => ({
    integrationThreadMessageIdx: index("idx_int_messages_integration_thread_message").on(table.integrationType, table.integrationId, table.channelId, table.threadTs, table.messageTs),
    integrationTypeIdx: index("idx_integrations_messages_integration_type").on(table.integrationType),
    uniqueMessageTs: uniqueIndex("integrations_messages_unique_message_ts").on(table.integrationType, table.integrationId, table.channelId, table.threadTs, table.messageTs),
}));
export const integrationCopilotLinks = pgTable("integrations_copilot_links", {
    id: uuid("id").primaryKey().defaultRandom(),
    conversationId: uuid("conversation_id")
        .notNull()
        .references(() => conversations.id, { onDelete: "cascade" }),
    integrationType: text("integration_type").notNull().$type(),
    integrationId: uuid("integration_id")
        .notNull()
        .references(() => organisationIntegrations.id, { onDelete: "cascade" }),
    teamId: text("team_id"),
    channelId: text("channel_id"),
    threadTs: text("thread_ts"),
    originLabel: text("origin_label"),
    slackLinkMessageTs: text("slack_link_message_ts"),
    pinnedAt: timestamp("pinned_at", { withTimezone: true }),
    isActive: boolean("is_active").notNull().default(false),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow()
        .$onUpdate(() => new Date()),
}, table => ({
    uniqueConversation: uniqueIndex("integrations_copilot_links_conversation_unique").on(table.conversationId),
    integrationThreadIdx: index("idx_integrations_copilot_thread").on(table.integrationType, table.integrationId, table.channelId, table.threadTs),
    integrationTypeIdx: index("idx_integrations_copilot_links_integration_type").on(table.integrationType),
    uniqueIntegrationThread: uniqueIndex("integrations_copilot_links_integration_thread_unique").on(table.integrationType, table.integrationId, table.teamId, table.channelId, table.threadTs),
}));
export const integrationMessagingSummaries = pgTable("integrations_messaging_summaries", {
    id: uuid("id").primaryKey().defaultRandom(),
    integrationType: text("integration_type").notNull().$type(),
    integrationId: uuid("integration_id")
        .notNull()
        .references(() => organisationIntegrations.id, { onDelete: "cascade" }),
    channelId: text("channel_id"),
    threadTs: text("thread_ts"),
    summaryText: text("summary_text").notNull(),
    structured: jsonb("structured").notNull().default({}),
    embeddingId: uuid("embedding_id").references(() => embeddings.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow()
        .$onUpdate(() => new Date()),
    expiresAt: timestamp("expires_at", { withTimezone: true }),
}, table => ({
    integrationSummaryIdx: index("idx_int_summaries_integration_channel_thread_created").on(table.integrationType, table.integrationId, table.channelId, table.threadTs, table.createdAt),
    integrationTypeIdx: index("idx_integrations_messaging_summaries_integration_type").on(table.integrationType),
}));
export const integrationChannelSettings = pgTable("integrations_channel_settings", {
    id: uuid("id").primaryKey().defaultRandom(),
    integrationType: text("integration_type").notNull().$type(),
    integrationId: uuid("integration_id")
        .notNull()
        .references(() => organisationIntegrations.id, { onDelete: "cascade" }),
    teamId: text("team_id"),
    channelId: text("channel_id").notNull(),
    passiveEnabled: text("passive_enabled").notNull().default("true"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow()
        .$onUpdate(() => new Date()),
}, table => ({
    uniqueChannel: uniqueIndex("integrations_channel_settings_unique").on(table.integrationType, table.integrationId, table.channelId),
    integrationTypeIdx: index("idx_integrations_channel_settings_integration_type").on(table.integrationType),
}));
export const integrationMessageLinks = pgTable("integrations_message_links", {
    id: uuid("id").primaryKey().defaultRandom(),
    integrationType: text("integration_type").notNull().$type(),
    integrationId: uuid("integration_id")
        .notNull()
        .references(() => organisationIntegrations.id, { onDelete: "cascade" }),
    conversationId: uuid("conversation_id"),
    slackChannelId: text("slack_channel_id"),
    slackThreadTs: text("slack_thread_ts"),
    slackMessageTs: text("slack_message_ts"),
    lucienMessageId: uuid("lucien_message_id"),
    actionId: uuid("action_id"),
    role: text("role"), // user|assistant|system|action
    metadata: jsonb("metadata").notNull().default({}),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow()
        .$onUpdate(() => new Date()),
}, table => ({
    uniqueSlackMessage: uniqueIndex("integrations_message_links_slack_unique").on(table.integrationType, table.integrationId, table.slackChannelId, table.slackMessageTs),
    uniqueLucienMessage: uniqueIndex("integrations_message_links_lucien_unique").on(table.lucienMessageId),
    uniqueAction: uniqueIndex("integrations_message_links_action_unique").on(table.actionId),
    integrationTypeIdx: index("idx_integrations_message_links_integration_type").on(table.integrationType),
}));
export const integrationUsers = pgTable("integrations_users", {
    id: uuid("id").primaryKey().defaultRandom(),
    integrationType: text("integration_type").notNull().$type(),
    integrationId: uuid("integration_id")
        .notNull()
        .references(() => organisationIntegrations.id, { onDelete: "cascade" }),
    userId: uuid("user_id").references(() => users.id, { onDelete: "set null" }),
    linkedAt: timestamp("linked_at", { withTimezone: true }),
    verifiedBy: text("verified_by").notNull().$type(),
    invitedBy: uuid("invited_by").references(() => users.id, { onDelete: "set null" }),
    metadata: jsonb("metadata").notNull().default({}),
    status: text("status").notNull().$type().default("active"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow()
        .$onUpdate(() => new Date()),
}, table => ({
    integrationUserIdx: index("idx_integration_users_integration_user").on(table.integrationType, table.integrationId, table.userId),
}));
